import React from "react";
import styled, { css } from "styled-components";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  FlipProp,
  IconDefinition,
  PullProp,
  RotateProp,
  SizeProp,
} from "@fortawesome/fontawesome-svg-core";

import { color } from "../../tokens";
import { ICONS } from "./icons";

type FAIconProps = React.ComponentProps<typeof FontAwesomeIcon>;

export interface Props extends Omit<FAIconProps, "icon" | "onClick" | "style"> {
  icon: keyof typeof ICONS;
  size?: SizeProp;
  fixedWidth?: boolean;
  rotation?: RotateProp;
  flip?: FlipProp;
  color?: string;
  border?: boolean;
  pull?: PullProp;
  animation?:
    | "beat"
    | "beatFade"
    | "bounce"
    | "fade"
    | "shake"
    | "spin"
    | "spinReverse"
    | "spinPulse";
  onClick?: () => void;
  disabled?: boolean;
  button?: boolean;
  buttonProps?: React.ComponentProps<typeof Button>;
}

/**
 * Get the font-size value for a given FontAwesome size prop.
 */
const getFontSizeForSize = (size: SizeProp): string => {
  const sizeMap: Record<SizeProp, string> = {
    "2xs": "0.625em",
    xs: "0.75em",
    sm: "0.875em",
    lg: "1.25em",
    xl: "1.5em",
    "2xl": "2em",
    "1x": "1em",
    "2x": "2em",
    "3x": "3em",
    "4x": "4em",
    "5x": "5em",
    "6x": "6em",
    "7x": "7em",
    "8x": "8em",
    "9x": "9em",
    "10x": "10em",
  };
  return sizeMap[size] || "1em";
};

/**
 * A convenience wrapper around the `FontAwesomeIcon` component, with some
 * extra props and defaults set.
 *
 * @prop icon {key}: name of the icon to display.
 * @prop size {string}: size of the icon [1x - 10x, and T-shirt sizes].
 * @prop color {string}: color of the icon.
 * @prop fixedWidth {boolean}: Whether the icon should have a fixed width for column display.
 * @prop rotation {90, 180, 270}: The rotation of the icon.
 * @prop flip {'horizontal', 'vertical', 'both'}: Flips the icon.
 * @prop border {boolean}: add a border.
 * @prop animation {"beat" | "beatFade" | "bounce" | "fade" | "shake" | "spin" | "spinPulse" | "spinReverse"}:  animation to apply to the icon.
 *
 * More styling details: https://docs.fontawesome.com/web/use-with/react/style
 */
export const Icon: React.FC<Props> = (props) => {
  const {
    icon,
    size = "1x",
    animation,
    disabled,
    button,
    buttonProps,
    onClick,
    ...rest
  } = props;

  const handleClick = () => {
    onClick?.();
  };

  const iconComponent = (
    <StyledFontAwesomeIcon
      icon={ICONS[icon] as IconDefinition}
      size={size}
      beat={animation === "beat"}
      beatFade={animation === "beatFade"}
      bounce={animation === "bounce"}
      fade={animation === "fade"}
      shake={animation === "shake"}
      spin={animation === "spin" || animation === "spinReverse"}
      spinReverse={animation === "spinReverse"}
      spinPulse={animation === "spinPulse"}
      $size={size}
      $cursor={onClick && !disabled ? "pointer" : "inherit"}
      $color={disabled ? color.neutral[400] : undefined}
      // if not wrapping in a button, attach the onClick directly
      onClick={!button && onClick && !disabled ? handleClick : undefined}
      {...rest}
    />
  );

  // wrap in a button if onClick is provided and button=true
  return button && onClick ? (
    <Button disabled={disabled} onClick={handleClick} {...buttonProps}>
      {iconComponent}
    </Button>
  ) : (
    iconComponent
  );
};

const Button = styled.button<{ disabled?: boolean }>`
  all: unset;
  padding: 0.6rem;
  border-radius: 50%;
  transition: background-color 0.3s ease;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;

  ${(props) =>
    (!props.disabled &&
      css`
        &:hover {
          background-color: ${color.blue[100]};
          cursor: pointer;
        }
        &:active {
          background-color: ${color.blue[200]};
        }
      `) ||
    css`
      opacity: 0.6;
    `}
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)<{
  $size?: SizeProp;
  $cursor?: string;
  $color?: string;
}>`
  font-size: ${(props) =>
    props.$size ? getFontSizeForSize(props.$size) : undefined};
  cursor: ${(props) => props.$cursor};
  color: ${(props) => props.$color};
`;
